import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useFusionConfig } from '../hooks/useFusionConfig'
import { copyText } from '../lib/clipboard'
import { buildComposition, buildPair, fusionMessage, generateOutputs, parseBatch } from '../lib/fusion'

type Pair = {
  id: string
  upper: string
  lower: string
  text: string
  assigned: boolean
  manager?: string
  hue?: number
}

type Composition = { id: string; text: string; hue: number }

type Snapshot = { pairs: Pair[]; compositions: Composition[]; selected: string | null }

const HUES = [210, 150, 340, 40, 270, 18, 180, 300, 90, 55, 240, 120]
const STORE_KEY = 'ds.fusion.work'

type Stored = {
  source: string
  prefix: string
  compatible: string
  escaped: string
  pairs: Pair[]
  compositions: Composition[]
  note: string
}

function readStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Stored
  } catch {
    return null
  }
}

function writeStored(value: Stored) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(value))
  } catch {
    /* 记不住就只在这一页有效 */
  }
}

export function Fusion() {
  const stored = readStored()
  const [tab, setTab] = useState<'source' | 'summary'>('source')
  const fusion = useFusionConfig()
  const cabinet = fusion.state
  const [source, setSource] = useState(stored?.source ?? '')
  const [prefix, setPrefix] = useState(stored?.prefix ?? '')
  const [compatible, setCompatible] = useState(stored?.compatible ?? '')
  const [escaped, setEscaped] = useState(stored?.escaped ?? '')
  const [pairs, setPairs] = useState<Pair[]>(stored?.pairs ?? [])
  const [compositions, setCompositions] = useState<Composition[]>(stored?.compositions ?? [])
  const [selected, setSelected] = useState<string | null>(null)
  const [undo, setUndo] = useState<Snapshot[]>([])
  const [note, setNote] = useState(stored?.note ?? '')
  const [copyNote, setCopyNote] = useState('')
  const [summaryNote, setSummaryNote] = useState(pairs.length ? '请先选择一组配对源。' : '')
  useEffect(() => {
    writeStored({ source, prefix, compatible, escaped, pairs, compositions, note })
  }, [source, prefix, compatible, escaped, pairs, compositions, note])

  function clearGenerated() {
    setPrefix('')
    setCompatible('')
    setEscaped('')
    setPairs([])
    setCompositions([])
    setSelected(null)
    setUndo([])
    setCopyNote('')
  }

  function generate() {
    const batch = parseBatch(source)
    if (batch.code !== 'OK') {
      clearGenerated()
      setNote(fusionMessage(batch.code))
      setSummaryNote('')
      return
    }
    const outputs = generateOutputs(batch.sources, cabinet)
    if (outputs.code !== 'OK') {
      clearGenerated()
      setNote(fusionMessage(outputs.code))
      setSummaryNote('')
      return
    }
    setPrefix(outputs.prefix)
    setCompatible(outputs.compatible)
    setEscaped(outputs.escapedCompatible)
    setCopyNote('')
    const next = batch.upper
      .map((upper, index) => ({ upper, lower: batch.lower[index] ?? '' }))
      .filter((slot) => slot.upper)
    setPairs(
      next.map((slot, index) => ({
        id: String(index),
        upper: slot.upper,
        lower: slot.lower,
        text: buildPair(slot.upper, slot.lower),
        assigned: false,
      })),
    )
    setCompositions([])
    setSelected(null)
    setUndo([])
    const solos = next.filter((slot) => !slot.lower).length
    const paired = next.length - solos
    const split = batch.autoSplit ? '已对半切分，' : ''
    const soloNote = solos ? `${solos} 条单独生成，2. 留空。` : ''
    setNote(`${split}共 ${batch.sources.length} 条源，${paired} 组配对。${soloNote}`)
    setSummaryNote(next.length ? '请先选择一组配对源。' : '没有可配对的源。')
  }

  async function copyBlock(value: string) {
    if (!value) {
      setNote('没有可复制的内容。')
      return
    }
    const ok = await copyText(value)
    setCopyNote(ok ? '已复制到剪贴板。' : '浏览器拒绝了剪贴板，请手动选择文本。')
  }

  function choosePair(id: string) {
    setSelected(id)
    setSummaryNote('已选择一组配对源，请点击管理员。')
  }

  function assign(manager: string) {
    const pair = pairs.find((item) => item.id === selected)
    if (!pair || pair.assigned) {
      setSummaryNote('请先从左侧选择一组配对源。')
      return
    }
    const hue = HUES[compositions.length % HUES.length]
    setUndo((stack) => [...stack, { pairs, compositions, selected }])
    setPairs((list) => list.map((item) => (item.id === pair.id ? { ...item, assigned: true, manager, hue } : item)))
    setCompositions((list) => [
      ...list,
      { id: pair.id + '-' + Date.now(), text: buildComposition(manager, pair.upper, pair.lower), hue },
    ])
    setSelected(null)
    setSummaryNote('已合成并加入输出区：' + manager)
  }

  function undoLast() {
    setUndo((stack) => {
      const snapshot = stack[stack.length - 1]
      if (!snapshot) return stack
      setPairs(snapshot.pairs)
      setCompositions(snapshot.compositions)
      setSelected(snapshot.selected)
      setSummaryNote('已撤销上一步配对。')
      return stack.slice(0, -1)
    })
  }

  function resetPairs() {
    setPairs((list) => list.map((item) => ({ ...item, assigned: false, manager: undefined, hue: undefined })))
    setCompositions([])
    setSelected(null)
    setUndo([])
    setSummaryNote('已重置所有配对。')
  }

  async function copyAll() {
    if (!compositions.length) {
      setSummaryNote('没有可复制的内容。')
      return
    }
    const ok = await copyText(compositions.map((item) => item.text).join('\n\n'))
    setSummaryNote(ok ? '已复制到剪贴板。' : '浏览器拒绝了剪贴板，请手动选择文本。')
  }

  return (
    <section className="fusion-page">
      <header className="page-head fusion-head">
        <div className="row">
          <button type="button" className={tab === 'source' ? 'primary' : ''} onClick={() => setTab('source')}>
            源数据输入
          </button>
          <button type="button" className={tab === 'summary' ? 'primary' : ''} onClick={() => setTab('summary')}>
            配对汇总
          </button>
        </div>
        <div className="fusion-title">
          <h1>融合器</h1>
          <p className="muted">生成带头、兼容、带头兼容。能配成对的配成对，多出来的一条单独生成，2. 留空。</p>
        </div>
      </header>

      {fusion.error && <p className="notice error">{fusion.error}</p>}

      {tab === 'source' && (
        <>
          <div className="fusion-layout">
            <section className="card">
              <label className="field">
                <span>源</span>
                <textarea
                  value={source}
                  spellCheck={false}
                  rows={12}
                  placeholder={'1716.ws\n6872.ws\n\nmok1sq2d4dpr.top\nm5kppufdyrp2.top'}
                  onChange={(e) => setSource(e.target.value)}
                />
              </label>
              <p className="muted">
                使用单独一行的 ~、/、@ 或空一行分隔上下两组。没有分界线时对半配对。两侧数量不同，或多出一条时，多出来的单独生成：1. 写域名，2. 留空。
              </p>
              <button type="button" className="primary" onClick={generate}>
                生成本批结果
              </button>
              {note && <p className="muted">{note}</p>}
              {copyNote && <p className="muted">{copyNote}</p>}
            </section>
            <section className="card">
              <h2>当前橱柜配置</h2>
              <p>
                橱柜编号 <b>{cabinet.cabinetNumber || '—'}</b>
              </p>
              <p>
                橱柜区域 <b>{cabinet.cabinetArea || '—'}</b>
              </p>
              <Link to="/fusion-config">更改配置</Link>
            </section>
          </div>
          <div className="fusion-results">
            <ResultCard title="带头" text={prefix} onCopy={() => void copyBlock(prefix)} />
            <ResultCard title="兼容" text={compatible} onCopy={() => void copyBlock(compatible)} />
            <ResultCard title="带头兼容" text={escaped} onCopy={() => void copyBlock(escaped)} />
          </div>
        </>
      )}

      {tab === 'summary' && (
        <>
          <div className="fusion-layout">
            <section className="card">
              <div className="row spread">
                <h2>配对源选择</h2>
                <div className="row">
                  <button type="button" onClick={undoLast} disabled={!undo.length}>
                    撤销
                  </button>
                  <button type="button" onClick={resetPairs} disabled={!compositions.length}>
                    重置
                  </button>
                </div>
              </div>
              {pairs.length === 0 ? (
                <p className="empty">请先在源数据输入里生成本批结果。</p>
              ) : (
                <div className="pair-list">
                  {pairs.map((pair) => (
                    <button
                      key={pair.id}
                      type="button"
                      className={
                        pair.assigned ? 'pair-source is-assigned' : selected === pair.id ? 'pair-source is-selected' : 'pair-source'
                      }
                      disabled={pair.assigned}
                      style={
                        pair.assigned && pair.hue != null
                          ? {
                              borderColor: `hsl(${pair.hue} 72% 52%)`,
                              background: `hsl(${pair.hue} 42% 16%)`,
                              color: 'var(--fg)',
                              boxShadow: `0 0 0 1px hsl(${pair.hue} 72% 52%)`,
                            }
                          : undefined
                      }
                      onClick={() => choosePair(pair.id)}
                    >
                      <span className="pair-text">{pair.text}</span>
                      {pair.manager && (
                        <span className="pair-name" style={{ background: `hsl(${pair.hue} 72% 46%)` }}>
                          {pair.manager}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
            <section className="card">
              <div className="row spread">
                <h2>管理员选择</h2>
                <Link to="/fusion-config">管理名单</Link>
              </div>
              {cabinet.managers.length === 0 ? (
                <p className="empty">暂无管理员，请先到配置页维护名单。</p>
              ) : (
                <div className="manager-grid">
                  {cabinet.managers.map((name) => (
                    <button key={name} type="button" onClick={() => assign(name)}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </section>
          </div>
          <section className="card">
            <div className="row spread">
              <h2>输出结果区</h2>
              <button type="button" onClick={resetPairs} disabled={!compositions.length}>
                清空本批
              </button>
            </div>
            {summaryNote && <p className="muted">{summaryNote}</p>}
            {compositions.length === 0 ? (
              <p className="empty">选择一组配对源，再选择管理员，即可在这里生成结果。</p>
            ) : (
              <div className="pair-list">
                {compositions.map((item) => (
                  <div key={item.id} className="composition-item" style={{ borderColor: `hsl(${item.hue} 70% 45%)` }}>
                    <pre>{item.text}</pre>
                    <button type="button" onClick={() => void copyText(item.text).then((ok) => setSummaryNote(ok ? '已复制到剪贴板。' : '复制失败'))}>
                      复制
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="primary" onClick={() => void copyAll()} disabled={!compositions.length}>
              一键复制
            </button>
          </section>
        </>
      )}
    </section>
  )
}

function ResultCard({ title, text, onCopy }: { title: string; text: string; onCopy: () => void }) {
  return (
    <section className="card">
      <div className="row spread">
        <h2>{title}</h2>
        <button type="button" onClick={onCopy} disabled={!text}>
          复制
        </button>
      </div>
      <pre className="result-pre">{text || '等待生成'}</pre>
    </section>
  )
}
